import without from 'lodash/without';
import { createLocalStorageProvider } from '@atlassian/jira-browser-storage-providers';
import type { PanelType } from '../../model/panels/types';
import type { BacklogCardGroupSettings, BacklogSettings } from './types';

const localStorage = createLocalStorageProvider('backlog');

const DETAIL_VIEW_WIDTH_RATIO = 'gh.detailViewWidth';
const BACKLOG_SETTINGS_PREFIX = 'gh.backlogSettings.';
const BACKLOG_CARDGROUP_SETTINGS_PREFIX = 'gh.backlogCardGroupSettings.';
const PANEL_STATE = 'panel-state';
const WORKLOAD_MODAL_SUBTASKS = 'gh.workloadModal.subtasks';

export {
	getHideEpics,
	setHideEpics,
	getHideStatuses,
	setHideStatuses,
	getHideVersions,
	setHideVersions,
} from './global-storage';

export const getDetailViewWidthRatio = (): number => {
	const value = localStorage.get(DETAIL_VIEW_WIDTH_RATIO);
	return value ? Number(value) : 0;
};
export const setDetailViewWidthRatio = (ratio: number) =>
	localStorage.set(DETAIL_VIEW_WIDTH_RATIO, ratio);

export const getBacklogSettings = (boardId: number): BacklogSettings => {
	const data = localStorage.get(`${BACKLOG_SETTINGS_PREFIX}${boardId}`);
	if (!data || !data.collapsedCardLists) {
		return {};
	}

	return data;
};

export const setBacklogSettings = (boardId: number, settings: BacklogSettings) => {
	localStorage.set(`${BACKLOG_SETTINGS_PREFIX}${boardId}`, settings);
};

export const getPanelStateOld = (): PanelType | null => localStorage.get(PANEL_STATE);

// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
export const getPanelState = (boardId: string): PanelType[] | void =>
	localStorage.get(`${PANEL_STATE}-${boardId}`);

export const setPanelStateOld = (panelState: PanelType | null) =>
	localStorage.set(PANEL_STATE, panelState);

export const setPanelState = (panelState: PanelType, boardId: string) => {
	const currentPanelState = getPanelState(boardId) || [];
	if (!currentPanelState.includes(panelState)) {
		localStorage.set(`${PANEL_STATE}-${boardId}`, [...currentPanelState, panelState]);
	}
};

export const setAllPanelState = (
	panels: Record<PanelType, boolean | undefined>,
	boardId: string,
) => {
	const openPanels = Object.entries(panels)
		.filter(([_, value]) => value === true)
		.map(([key]) => key);
	localStorage.set(`${PANEL_STATE}-${boardId}`, openPanels);
};

export const unsetPanelState = (panelState: PanelType, boardId: string) => {
	const currentPanelState = getPanelState(boardId);
	if (currentPanelState?.includes(panelState)) {
		localStorage.set(`${PANEL_STATE}-${boardId}`, without(currentPanelState, panelState));
	}
};

export const getBacklogCardGroupSettings = (boardId: number): BacklogCardGroupSettings => {
	const data = localStorage.get(`${BACKLOG_CARDGROUP_SETTINGS_PREFIX}${boardId}`);
	return data ?? {};
};

export const setBacklogCardGroupSettings = (
	boardId: number,
	settings: BacklogCardGroupSettings,
) => {
	localStorage.set(`${BACKLOG_CARDGROUP_SETTINGS_PREFIX}${boardId}`, settings);
};

/** Get the "include subtask" setting from local storage */
export const getWorkloadSubtaskSettings = (): boolean => {
	const data = localStorage.get(WORKLOAD_MODAL_SUBTASKS);
	return !!data;
};

/** Set the "include subtask" setting in local storage */
export const setWorkloadSubtaskSettings = (includeSubtasks: boolean) => {
	localStorage.set(WORKLOAD_MODAL_SUBTASKS, includeSubtasks);
};
